import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../lib/api';
import type { NoteTemplate, Subject } from '@repo/shared-types';
import type { CreateSubjectInput, UpdateSubjectInput } from '@repo/validation';
import { toast } from 'sonner';
import { invalidateGettingStarted } from './useGettingStarted';

export function useSubjects() {
  return useQuery<Subject[]>({ queryKey: ['subjects'], queryFn: () => fetchApi('/subjects') });
}

export function useCreateSubject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSubjectInput) =>
      fetchApi<Subject>('/subjects', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subjects'] });
      invalidateGettingStarted(qc); // checklist step 1
      toast.success('Đã tạo môn học');
    },
    onError: (e) => toast.error(e.message || 'Không thể tạo môn học'),
  });
}

export function useUpdateSubject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSubjectInput) =>
      fetchApi<Subject>(`/subjects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: (_row, data) => {
      qc.invalidateQueries({ queryKey: ['subjects'] });
      // THI-346: the switch travels on the notes payload and gates the timeline.
      if (data.sessionRatingEnabled !== undefined) {
        qc.invalidateQueries({ queryKey: ['session-notes'] });
        qc.invalidateQueries({ queryKey: ['progress-timeline'] });
      }
    },
    onError: (e) => toast.error(e.message || 'Không thể cập nhật môn học'),
  });
}

export function useDeleteSubject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<{ deleted: boolean }>(`/subjects/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subjects'] });
      toast.success('Đã xoá môn học');
    },
    onError: (e) => toast.error(e.message || 'Không thể xoá môn học'),
  });
}

/** THI-193: PUT the whole template, or `null` to remove it ("Bỏ sườn"). The
 *  subjects list is the only subject query, so invalidating it refreshes the
 *  detail page; session-note forms re-read the template on their next GET. */
export function useSaveNoteTemplate(subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (template: NoteTemplate | null) =>
      fetchApi<Subject>(`/subjects/${subjectId}/note-template`, { method: 'PUT', body: JSON.stringify(template) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subjects'] });
      qc.invalidateQueries({ queryKey: ['session-notes'] });
    },
    onError: (e) => toast.error(e.message || 'Không thể lưu sườn nhận xét'),
  });
}
